import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';

import { Logger } from '../../helpers/Logger';
import { AddressController } from '../../networking/controllers/AddressController';
import { BasicInput } from '../../common/basic-input';
import { Spinner } from '../../common/spinner';
import styles from './StreetAutocompleteInputV2.css';

const IPropTypes = {
  className: PropTypes.string,
  currentDepartment: PropTypes.string,
  currentLocation: PropTypes.string,
  currentLocationid: PropTypes.number,
  onChange: PropTypes.func,
  value: PropTypes.string,
};

const defaultProps = {
  className: '',
  currentDepartment: null,
  currentLocation: null,
  currentLocationid: null,
  onChange: () => {},
  value: null,
};

const MINIMUM_TEXT_LENGTH = 3;
const SUGGESTION_TIMEOUT = 500;

class StreetAutocompleteInputV2 extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      streetSuggestions: null,
      suggestionInProgress: false,
    };

    this.container = null;
    // Used to avoid race conditions on suggestion requests.
    this.suggestionRequestId = 0;
    this.suggestionTimeoutSet = false;
    this.suggestionText = '';

    this._setContainer = this._setContainer.bind(this);
    this._handleDocumentClick = this._handleDocumentClick.bind(this);
    this._queryForSuggestions = this._queryForSuggestions.bind(this);
    this._getStreetSuggestions = this._getStreetSuggestions.bind(this);
    this._clearStreetSuggestions = this._clearStreetSuggestions.bind(this);
    this._updateText = this._updateText.bind(this);
  }

  componentDidMount() {
    document.addEventListener('click', this._handleDocumentClick, false);
    document.addEventListener('touchend', this._handleDocumentClick, false);
  }

  componentWillUnmount() {
    document.removeEventListener('click', this._handleDocumentClick, false);
    document.removeEventListener('touchend', this._handleDocumentClick, false);
  }

  _setContainer(element) {
    this.container = element;
  }

  _handleDocumentClick(event) {
    if (this.container && !this.container.contains(event.target)) {
      if (this.state.streetSuggestions) {
        this._clearStreetSuggestions();
      }
    }
  }

  _updateText(event) {
    const { target: { value } } = event;
    if (value) {
      this._getStreetSuggestions(value);
    } else {
      this._clearStreetSuggestions();
    }
    this.props.onChange(value);
  }

  _suggestionClickCallback(suggestion) {
    return (event) => {
      event.preventDefault();
      this._clearStreetSuggestions();
      this.props.onChange({
        id: suggestion.id,
        name: suggestion.street,
      });
    };
  }

  _clearStreetSuggestions() {
    // Ignore all pending requests
    this.suggestionRequestId += 1;
    this.setState({
      streetSuggestions: null,
    });
  }

  async _queryForSuggestions() {
    this.suggestionRequestId += 1;
    const requestId = this.suggestionRequestId;
    this.setState({
      suggestionInProgress: true,
    });
    this.suggestionTimeoutSet = false;
    try {
      const suggestions = await AddressController.streetSuggestionNewApi(
        this.suggestionText,
        this.props.currentLocationid,
      );
      if (requestId !== this.suggestionRequestId) {
        return;
      }
      this.setState({
        streetSuggestions: suggestions.slice(0, 10),
      });
    } catch (error) {
      Logger.warn(error);
    } finally {
      this.setState({
        suggestionInProgress: false,
      });
    }
  }

  _getStreetSuggestions(text) {
    if (text.length < MINIMUM_TEXT_LENGTH) {
      return;
    }
    this.suggestionText = text;
    if (!this.suggestionTimeoutSet && !this.suggestionInProgress) {
      this._queryForSuggestions();
    } else if (!this.suggestionTimeoutSet) {
      this.suggestionTimeoutSet = true;
      window.setTimeout(this._queryForSuggestions, SUGGESTION_TIMEOUT);
    }
  }

  render() {
    const propsCopy = { ...this.props };
    delete propsCopy.currentDepartment;
    delete propsCopy.currentLocation;

    return (
      <div
        className={styles.container}
        ref={this._setContainer}
      >
        <BasicInput
          {...propsCopy}
          className={[this.props.className, styles.input].join(' ')}
          onChange={this._updateText}
          value={this.props.value}
        />
        { this.state.suggestionInProgress &&
          <Spinner className={styles.spinner} imgClassName={styles.spinnerImage} />
        }
        {
          this.state.streetSuggestions &&
          !!this.state.streetSuggestions.length &&
          <div className={styles.dropdown}>
            {
              this.state.streetSuggestions.map(suggestion => (
                <button
                  key={suggestion.id}
                  className={styles.suggestion}
                  onClick={this._suggestionClickCallback(suggestion)}
                >
                  {suggestion.street}
                </button>
              ))
            }
          </div>
        }
      </div>
    );
  }
}

StreetAutocompleteInputV2.propTypes = IPropTypes;
StreetAutocompleteInputV2.defaultProps = defaultProps;

const ObservedStreetAutocompleteInput = observer(StreetAutocompleteInputV2);

export { ObservedStreetAutocompleteInput as StreetAutocompleteInputV2 };



// WEBPACK FOOTER //
// ./src/common/street-autocomplete-input-v2/StreetAutocompleteInputV2.jsx